import { Controller } from "@hotwired/stimulus"
import { post, get } from '@rails/request.js'

export default class extends Controller {
  static targets = ["input", "dropdown", "labelTag", "tagInputContainer"]
  static values = {
    container: String,
    searchUrl: String,
    url: String,
    type: String,
    id: Number
  }

  connect() {
    this.filtered = []
    this.preSelected = null
    this.value = ""
    this.editing = false
    this.clickHandler = this.handleClickOutside.bind(this)
  }

  disconnect() {
    this.removeClickOutsideHandler()
  }

  startEditing() {
    this.editing = true
    this.tagInputContainerTarget.classList.remove("is-hidden")
    this.labelTagTarget.classList.add("is-hidden")

    // Add click outside handler
    document.addEventListener('click', this.clickHandler)

    // Focus on input field
    setTimeout(() => {
      this.inputTarget.focus()
      this.applyFilter()
    }, 0)
  }

  stopEditing() {
    this.editing = false
    this.tagInputContainerTarget.classList.add("is-hidden")
    this.labelTagTarget.classList.remove("is-hidden")
    this.removeClickOutsideHandler()
  }

  removeClickOutsideHandler() {
    document.removeEventListener('click', this.clickHandler)
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.stopEditing()
    }
  }

  handleKeyup(event) {
    // Update the value
    this.value = event.target.value

    if (event.key === 'Enter') {
      this.addTag()
    } else if (event.key === 'Escape') {
      this.stopEditing()
    } else if (event.key === 'ArrowDown') {
      const index = this.filtered.indexOf(this.preSelected)
      const nextIndex = index + 1
      this.preSelected = this.filtered[nextIndex] || this.filtered[0]
      this.highlightSelected()
    } else if (event.key === 'ArrowUp') {
      const index = this.filtered.indexOf(this.preSelected)
      const nextIndex = index - 1
      this.preSelected = this.filtered[nextIndex] || this.filtered[this.filtered.length - 1]
      this.highlightSelected()
    } else {
      this.debouncedApplyFilter()
    }
  }

  highlightSelected() {
    this.dropdownTarget.querySelectorAll('.dropdown-item').forEach(item => {
      if (item.textContent.trim() === this.preSelected) {
        item.classList.add('is-active')
      } else {
        item.classList.remove('is-active')
      }
    })
  }

  debouncedApplyFilter() {
    clearTimeout(this.filterTimeout)
    this.filterTimeout = setTimeout(() => {
      this.applyFilter()
    }, 300)
  }

  async applyFilter() {
    const tags = await this.retrieveTags()
    this.filtered = tags

    if (this.filtered.length > 0) {
      this.dropdownTarget.querySelector('.dropdown-menu').classList.remove('is-hidden')
      this.renderDropdown()
    } else {
      this.dropdownTarget.querySelector('.dropdown-menu').classList.add('is-hidden')
    }
  }

  async retrieveTags() {
    try {
      const response = await get(this.searchUrlValue, {
        query: {
          taggable_type: this.typeValue,
          taggable_id: this.idValue,
          q: this.value
        }
      })

      if (response.ok) {
        const body = await response.json
        return body.tags
      }
    } catch (error) {
      console.error("Error retrieving tags:", error)
    }
    return []
  }

  renderDropdown() {
    // Clear dropdown
    const dropdownContent = this.dropdownTarget.querySelector('.dropdown-content')
    dropdownContent.innerHTML = ''

    // Add items
    this.filtered.forEach(tag => {
      const item = document.createElement('a')
      item.classList.add('dropdown-item')
      item.textContent = tag
      if (this.preSelected === tag) {
        item.classList.add('is-active')
      }
      item.addEventListener('click', () => this.select(tag))
      dropdownContent.appendChild(item)
    })
  }

  select(item) {
    this.preSelected = item
    this.inputTarget.value = item
    this.value = item
    this.addTag()
  }

  addTag() {
    const tag = this.preSelected && this.filtered.includes(this.preSelected)
      ? this.preSelected
      : this.value

    if (tag === '') return

    post(this.urlValue, {
      body: JSON.stringify({
        tag,
        taggable_type: this.typeValue,
        taggable_id: this.idValue
      })
    }).then(() => {
      // Reset the input
      this.inputTarget.value = ''
      this.value = ''
      this.preSelected = null
      this.dropdownTarget.querySelector('.dropdown-menu').classList.add('is-hidden')
    })
  }
}
